import React, { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";

import _ from "lodash";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import Typography from "@material-ui/core/Typography";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

import { leadWithZero } from "../../../utilities";
import {
  selectProject,
  selectProjectDocumentCategory,
  selectProjectDocuments,
  metaDataViewOpen,
} from "../projectSelectors";

import {
  selectDocumentType,
  selectDocumentSubType,
} from "../../document/documentSelectors";
import { ProjectDocumentsNoResults } from "./ProjectDocumentsNoResults";
import ProjectDocumentsTable from "./ProjectDocumentsTable";
import ProjectDocumentMetaDataPage from "./ProjectDocumentMetaDataPage";

import styles from "./ProjectDocuments.module.scss";
import CommentsPanel from "../../comment/commentPanel/CommentsPanel";
import { viewOnlyWeight, getMaxWeight, externalUserWeight } from "../../../roleaccess/utils";

import * as commentActions from "../../comment/commentActions";
import {
  fetchDocProj,
  fetchDocStatusByProj,
  setDocStatusByProj,
  loadingDocStatusByProj,
  setLastOpenedProject,
  sortDocList,
  fetchDocTypes,
  fetchDocSubTypes,
  setMetaDataForProject,
  resetDocumentVersionsData,
  openUploadDocumentModal,
  closeUploadDocumentModal,
  openLinkDocumentsModal,
  closeLinkDocumentsModal,
} from "../../document/documentActions";
import UploadNewDocument from "./UploadNewDocument";
import LinkDocuments from "./LinkDocuments";
import { errorOpenModal } from "../../_shared/modal/errorModalActions";
import { selectUserRoles } from "../../login/loginSelector";
import * as actions from "../projectActions";
import { fetch as fetchCategories } from "../projectCategoryActions";

import { REL_ADMIN } from "../../../roleaccess/adGroups";

const drawerWidth = "37%";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth})`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  },
  title: {
    flexGrow: 1,
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    'background-color': '#e1edf9',
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  paper: {
    // padding: theme.spacing(2),
  },
  floatLeft: {
    float: "left",
  },
  floatNone: {
    float: "none",
    marginBottom: "10px",
  },
  margin: {
    margin: "20px",
  },
  marginBottom: {
    marginBottom: "20px",
  },
  marginTop: {
    marginTop: "20px",
  },
}));

const canEdit = closingStatus =>
  !!closingStatus && /^active$/i.test(closingStatus);

const mapStateToProps = (state, ownProps) => {
  const params = _.get(ownProps, "match.params", {});
  const projectID = _.get(params, "projectId", null);
  const projectName = _.get(state, "projectDetails.data.projectDetails.closing_name", {});
  const statusCode = _.get(state, "projectDetails.data.projectDetails.status_code", {});
  const documentsCategoryID = _.get(params, "documentsCategoryId", null);
  const documentCategory = selectProjectDocumentCategory(
    state,
    documentsCategoryID,
  );
  const documents = selectProjectDocuments(state);
  const documentTypes = selectDocumentType(state);
  const documentSubTypes = selectDocumentSubType(state);
  const project = selectProject(state, projectID);
  const closingStatus = project.projectDetails ? project.projectDetails.closingStatus : '';
  const canAddNewDocuments = canEdit(closingStatus);
  const documentCategoryID = _.get(documentCategory, "doc_category_id", "");
  const isMetaDataViewOpen = metaDataViewOpen(state);
  const isUploadDialogOpen = _.get(
    state,
    "document.uploadNewDocModal.open",
    false,
  );
  const isLinkDocumentsDialogOpen = _.get(
    state,
    "document.linkDocumentsModal.open",
    false,
  );
  //const userRole = _.get(state, "session.user.data.userRole");
  const userRole = selectUserRoles(state);
  const isLoading = _.get(state, "document.request.loading", false);
  const storeNumber = _.get(state, "projectDetails.data.projectDetails.store_nbr", "");
  const companyNumber = _.get(state, "projectDetails.data.projectDetails.jde_company_nbr", "").trim();
  const documentStatusByProject = _.get(state, "document.documentStatusByProject.data", {});
  const isDocStatusLoading = _.get(state, "document.documentStatusByProject.request.loading", false);
  const lastOpenedProjectId = _.get(state, "document.lastOpenedProjectId", null);
  return {
    canAddNewDocuments,
    documentCategoryIndex: documentsCategoryID,
    documentCategoryID: _.get(documentCategory, "doc_category_id", ""),
    documentCategoryDescription: _.get(documentCategory, "category_name", ""),
    documents,
    documentTypes,
    documentSubTypes,
    projectID,
    isMetaDataViewOpen,
    projectName,
    isUploadDialogOpen,
    isLinkDocumentsDialogOpen,
    statusCode,
    userRole,
    isLoading,
    storeNumber,
    companyNumber,
    documentStatusByProject,
    isDocStatusLoading,
    lastOpenedProjectId,
  };
};

const mapDispatchToProps = dispatch => ({
  fetchProjectComments: closingId => dispatch(commentActions.fetch(closingId)),
  fetchCategoryComments: categoryId =>
    dispatch(commentActions.fetchCategoryComments(categoryId)),
  fetchAllCategoryCommentsForProject: closingId =>
    dispatch(commentActions.fetchAllCategoryCommentsForProject(closingId)),
  resetComments: () => dispatch({ type: commentActions.RESET_DATA }),
  fetchDocument: projectID => dispatch(fetchDocProj(projectID)),
  fetchDocStatusByProject: projectID => dispatch(fetchDocStatusByProj(projectID)),
  resetDocStatusByProject: () => dispatch(setDocStatusByProj({})),
  setDocStatusLoading: isLoading => dispatch(loadingDocStatusByProj(isLoading)),
  setLastOpenedProject: projectId => dispatch(setLastOpenedProject(projectId)),
  sortDocList: data => dispatch(sortDocList(data)),
  fetchDocumentTypes: () => {
    dispatch(fetchDocTypes());
  },
  fetchDocumentSubTypes: docType => dispatch(fetchDocSubTypes(docType)),
  openErrorModel: (title, message) => dispatch(errorOpenModal({ title, message })),
  isMetaDataView: (metaDataView, document) =>
    dispatch(actions.setMetaDataView(metaDataView, document)),
  changeSelectedProjectMetadata: metadata =>
    dispatch(setMetaDataForProject(metadata)),
  resetDocumentVersionsData: () => dispatch(resetDocumentVersionsData()),
  openUploadDocModal: () => dispatch(openUploadDocumentModal()),
  closeUploadDocModal: () => dispatch(closeUploadDocumentModal()),
  openLinkDocModal: () => dispatch(openLinkDocumentsModal()),
  closeLinkDocModal: () => dispatch(closeLinkDocumentsModal()),
  fetchProject: closingId => dispatch(actions.fetch(closingId)),
  fetchProjectCategories: closingId => dispatch(fetchCategories(closingId)),
});

export const ProjectDocuments = ({
  canAddNewDocuments,
  documentCategoryDescription,
  documentCategoryID,
  projectID,
  documentCategoryIndex,
  documents,
  documentTypes,
  documentSubTypes,
  fetchProjectComments,
  fetchCategoryComments,
  fetchAllCategoryCommentsForProject,
  resetComments,
  fetchDocument,
  fetchDocStatusByProject,
  resetDocStatusByProject,
  setDocStatusLoading,
  setLastOpenedProject,
  sortDocList,
  fetchDocumentTypes,
  fetchDocumentSubTypes,
  openErrorModel,
  isMetaDataViewOpen,
  isMetaDataView,
  projectName,
  changeSelectedProjectMetadata,
  resetDocumentVersionsData,
  isUploadDialogOpen,
  openUploadDocModal,
  closeUploadDocModal,
  isLinkDocumentsDialogOpen,
  openLinkDocModal,
  closeLinkDocModal,
  statusCode,
  userRole,
  isLoading,
  fetchProject,
  fetchProjectCategories,
  storeNumber,
  companyNumber,
  documentStatusByProject,
  isDocStatusLoading,
  lastOpenedProjectId,
}) => {
  let headingToShow =
    leadWithZero(documentCategoryIndex) + " - " + documentCategoryDescription;
  let subHeading = "";
  let headingGridWidth = 6;

  //const canViewComments = userRole ? !userRole.includes(REL_ADMIN) : true;
  const classes = useStyles();
  const [openUploadDialog, setOpenUploadDialog] = React.useState(false);

  const documentsOfCat = documents.filter(function (doc) {
    return doc.doc_category_id === documentCategoryID;
  });

  if (
    null != isMetaDataViewOpen &&
    undefined != isMetaDataViewOpen &&
    null != isMetaDataViewOpen.selectedDocument &&
    undefined != isMetaDataViewOpen.selectedDocument
  ) {
    if (
      isMetaDataViewOpen.selectedDocument.doc_category_id != documentCategoryID
    ) {
      clearSelectedDocumentForMetadataView();
    } else {
      headingToShow = isMetaDataViewOpen.selectedDocument.closing_doc_name;
      headingGridWidth = 11;
      subHeading = projectName + " > " + documentCategoryDescription;
    }
  }

  useEffect(() => {
    const parsedProjectID = Number(projectID);
    if (!isNaN(parsedProjectID)) {
      const isNotSameProject = lastOpenedProjectId !== parsedProjectID;
      if (isNotSameProject) {
        resetDocStatusByProject();
        setLastOpenedProject(0);
        setDocStatusLoading(true);
      }
      fetchDocument(parsedProjectID);
      fetchDocStatusByProject(parsedProjectID);
      fetchDocumentTypes();
    }
  }, [projectID]);

  useEffect(() => {
    if (!_.isEmpty(documentStatusByProject) && !isDocStatusLoading) {
      const parsedId = Number(projectID);
      if (lastOpenedProjectId !== parsedId) {
        setLastOpenedProject(parsedId);
      }
    }
  }, [documentStatusByProject, isDocStatusLoading, lastOpenedProjectId, projectID]);

  const [open, setOpen] = React.useState(false);
  function handleDrawerOpen(categoryId) {
    fetchProjectComments(parseInt(projectID));
    fetchAllCategoryCommentsForProject(parseInt(projectID));
    setOpen(true);
  }
  function handleDrawerClose() {
    resetComments();
    setOpen(false);
    fetchProject(parseInt(projectID));
  }
  function clearSelectedDocumentForMetadataView() {
    isMetaDataView(false, null);
    changeSelectedProjectMetadata({
      r_object_id: "",
      object_name: "",
      i_chronicle_id: "",
      r_version_label: "",
      r_creation_date: "",
      r_modify_date: "",
      owner_name: "",
      acl_name: "",
      doc_subtype_desc: "",
      originating_dept: "",
      doc_type: "",
      ecm_attr_doc_date: "",
      doc_type_desc: "",
      doc_status: "",
    });
    resetDocumentVersionsData();
  }

  const invertDirection = {
    asc: "desc",
    desc: "asc",
  };
  const [sortDirection, setSortDirection] = React.useState("desc");
  const canNotUploadDoc = getMaxWeight(userRole) == viewOnlyWeight();
  const canShowLinkDocuments = getMaxWeight(userRole) !== externalUserWeight();

  const handleSorting = column => {
    setSortDirection(invertDirection[sortDirection]);

    let sortedColumn;

    if (column == "Document Name") {
      sortedColumn = "closing_doc_name";
    } else if (column === "Created By") {
      sortedColumn = "create_userid";
    } else if (column === "Created Date") {
      sortedColumn = "create_ts";
    } else if (column == "Document Status") {
      sortedColumn = "public_view_status";
    } else if (column == "Last Modified") {
      sortedColumn = "last_change_ts";
    } else if (column == "Last Modified By") {
      sortedColumn = "last_change_userid";
    } else {
      // Default fallback to prevent undefined sortedColumn
      sortedColumn = "closing_doc_name";
    }
    let documentsSorted = _.orderBy(documents, [sortedColumn], [sortDirection]);
    sortDocList(documentsSorted);
  };
  return (
    <Grid container justify='center' spacing={3}>
      <Grid item xs={12}>
        <header className={styles.header}>
          <Grid container justify='flex-start' spacing={1}>
            {isMetaDataViewOpen.metaDataViewOpen ? (
              <Grid item xs={1} sm={1}>
                <Button
                  onClick={() => {
                    clearSelectedDocumentForMetadataView();
                  }}>
                  <ChevronLeft />
                </Button>
              </Grid>
            ) : (
              <span />
            )}
            <Grid item xs={headingGridWidth} sm={headingGridWidth}>
              <Typography variant="h5">{headingToShow}</Typography>
              {isMetaDataViewOpen.metaDataViewOpen ? (
                <Typography
                  variant="h6"
                  style={{
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontSize: '1.2rem',
                  }
                  }
                >
                  {subHeading}
                </Typography>
              ) : (
                <span />
              )}
            </Grid>
            {!isMetaDataViewOpen.metaDataViewOpen ? (
              <Grid item xs={6} sm={6} style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                {!isLoading && (
                  <Button
                    variant='contained'
                    color='primary'
                    className='viewCommentsButtonDocuments'
                    onClick={() => {
                      handleDrawerOpen(documentCategoryID);
                    }}>
                    View Comments
                  </Button>
                )}
                {(!canNotUploadDoc && !isLoading && statusCode != '2') && (
                  <Button
                    variant='contained'
                    color='primary'
                    className='viewCommentsButtonDocuments'
                    onClick={() => {
                      openUploadDocModal();
                    }}>
                    Add new document
                  </Button>
                )}
                {(!canNotUploadDoc && canShowLinkDocuments && !isLoading && statusCode != '2') && (
                  <Button
                    variant='contained'
                    color='primary'
                    className='viewCommentsButtonDocuments'
                    onClick={() => {
                      openLinkDocModal();
                    }}>
                    Link Documents
                  </Button>
                )}
              </Grid>
            ) : (
              <span />
            )}
          </Grid>
        </header>
      </Grid>
      {isLoading && <CircularProgress />}
      <Grid item xs={12}>
        {documentsOfCat.length ? (
          isMetaDataViewOpen.metaDataViewOpen ? (
            <ProjectDocumentMetaDataPage
              document={isMetaDataViewOpen.selectedDocument}
              openErrorModel={openErrorModel}
              categoryId={documentCategoryID}
              projectId={projectID}
              statusCode={statusCode}
            />
          ) : (
            <ProjectDocumentsTable
              documents={documentsOfCat}
              handleSorting={handleSorting}
              sortDirection={sortDirection}
              openErrorModel={openErrorModel}
              userRole={userRole}
              statusCode={statusCode}
              onLinkStatusUpdated={() => {
                fetchDocument(Number.parseInt(projectID));
                fetchProjectCategories(Number.parseInt(projectID));
              }}
              documentStatusByProject={documentStatusByProject}
              isDocStatusLoading={isDocStatusLoading}
              lastOpenedProjectId={lastOpenedProjectId}
              currentProjectId={projectID}
            />
          )
        ) : (
          !isLoading && <ProjectDocumentsNoResults />
        )}
        <Drawer
          className={classes.drawer}
          anchor='right'
          open={open}
          onClose={handleDrawerClose}
          classes={{
            paper: classes.drawerPaper,
          }}>
          <CommentsPanel
            categoryName={documentCategoryDescription}
            categoryId={documentCategoryID}
            isProjectLevel={false}
            statusCode={statusCode}
          />
        </Drawer>
        <UploadNewDocument
          documentTypes={documentTypes}
          documentSubTypes={documentSubTypes}
          fetchDocumentSubTypes={fetchDocumentSubTypes}
          open={isUploadDialogOpen}
          categoryId={documentCategoryID}
          projectId={projectID}
          handleClose={() => {
            closeUploadDocModal();
          }}
        />
        <LinkDocuments
          open={isLinkDocumentsDialogOpen}
          documentTypes={documentTypes}
          storeNumber={storeNumber}
          companyNumber={companyNumber}
          projectId={projectID}
          categoryId={documentCategoryID}
          existingDocuments={documents}
          onLinkCompleted={() => {
            fetchDocument(Number.parseInt(projectID));
            fetchProjectCategories(Number.parseInt(projectID));
            fetchDocStatusByProject(Number.parseInt(projectID));
          }}
          handleClose={() => {
            closeLinkDocModal();
          }}
        />
      </Grid>
    </Grid>
  );
};

ProjectDocuments.defaultProps = {
  canAddNewDocuments: false,
  documentCategoryDescription: "",
  documentCategoryID: "",
  projectID: "",
  documents: [],
};

ProjectDocuments.propTypes = {
  canAddNewDocuments: PropTypes.bool,
  documentCategoryDescription: PropTypes.string,
  documentCategoryID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      closing_doc_name: PropTypes.string,
      last_change_userid: PropTypes.string,
    }),
  ),
  fetchCategoryComments: PropTypes.func.isRequired,
  resetComments: PropTypes.func.isRequired,
  fetchDocument: PropTypes.func,
  fetchProjectCategories: PropTypes.func,
  sortDocList: PropTypes.func,
  fetchDocumentTypes: PropTypes.func,
  fetchDocumentSubTypes: PropTypes.func,
  openErrorModel: PropTypes.func.isRequired,
  isUploadDialogOpen: PropTypes.bool.isRequired,
  openUploadDocModal: PropTypes.func.isRequired,
  closeUploadDocModal: PropTypes.func.isRequired,
  isLinkDocumentsDialogOpen: PropTypes.bool.isRequired,
  openLinkDocModal: PropTypes.func.isRequired,
  closeLinkDocModal: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const reduxConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProjectDocuments);

export default withRouter(reduxConnected);
